import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../states/hooks";
import {
  fetchAttendance,
  clearError as clearAttendanceError,
  selectAttendance,
  selectAttendanceLoading,
  selectAttendanceError,
} from "../../states/attendance/AttendanceSlice";
import {
  fetchClass,
  selectCurrentClass,
} from "../../states/classes/ClassesSlice";
import { useBase } from "../../context/BaseContext";
import { ChevronLeftIcon, EmptyBoxIcon } from "../../assets/svg";
import "../dashboard/Dashboard.css";

const toDateInput = (d: Date) => d.toISOString().slice(0, 10);

const AttendancePage: React.FC = () => {
  const { classId = "" } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const records = useAppSelector(selectAttendance);
  const isLoading = useAppSelector(selectAttendanceLoading);
  const error = useAppSelector(selectAttendanceError);
  const currentClass = useAppSelector(selectCurrentClass);
  const { showError } = useBase();

  const today = useMemo(() => new Date(), []);
  const monthAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  }, []);

  const [from, setFrom] = useState(toDateInput(monthAgo));
  const [to, setTo] = useState(toDateInput(today));

  useEffect(() => {
    if (classId) dispatch(fetchClass(classId));
  }, [dispatch, classId]);

  useEffect(() => {
    if (error) {
      showError("Lỗi", error);
      dispatch(clearAttendanceError());
    }
  }, [error, showError, dispatch]);

  const handleQuery = () => {
    if (!classId) return;
    // Convert date-only strings to ISO with day bounds
    const fromISO = from ? new Date(`${from}T00:00:00.000Z`).toISOString() : undefined;
    const toISO = to ? new Date(`${to}T23:59:59.999Z`).toISOString() : undefined;
    dispatch(fetchAttendance({ classId, from: fromISO, to: toISO }));
  };

  useEffect(() => {
    handleQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  const handleExport = () => {
    if (records.length === 0) {
      showError("Không có dữ liệu", "Chưa có bản ghi điểm danh để xuất.");
      return;
    }
    const rows = records.map((r, i) => {
      const dt = new Date(r.takenAt);
      return {
        STT: i + 1,
        "Học sinh": r.student?.name || "(không xác định)",
        "Ngày": dt.toLocaleDateString("vi-VN"),
        "Thời gian": dt.toLocaleTimeString("vi-VN"),
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 6 },
      { wch: 30 },
      { wch: 14 },
      { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DiemDanh");

    const safeName = (currentClass?.name || "class").replace(/[^\w-]+/g, "_");
    const filename = `attendance_${safeName}_${from}_${to}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="dashboard-container">
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
        <IconButton onClick={() => navigate("/classes")} size="small">
          <ChevronLeftIcon className="w-6 h-6" />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {currentClass?.name || "Lớp"} — Lịch sử điểm danh
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          label="Từ ngày"
          type="date"
          size="small"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Đến ngày"
          type="date"
          size="small"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          onClick={handleQuery}
          sx={{ textTransform: "none" }}
        >
          Truy vấn
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          color="success"
          onClick={handleExport}
          disabled={records.length === 0}
          sx={{ textTransform: "none" }}
        >
          Xuất Excel ({records.length})
        </Button>
      </Paper>

      <div className="dashboard-table-container">
        {isLoading ? (
          <div className="dashboard-loading">
            <div className="dashboard-loading-content">
              <div className="dashboard-loading-spinner" />
              <p className="dashboard-loading-text">Đang tải...</p>
            </div>
          </div>
        ) : records.length === 0 ? (
          <div className="dashboard-empty">
            <EmptyBoxIcon className="dashboard-empty-icon" />
            <h3 className="dashboard-empty-title">Chưa có bản ghi</h3>
            <p className="dashboard-empty-description">
              Chưa có điểm danh trong khoảng ngày đã chọn.
            </p>
          </div>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
            <Table aria-label="attendance table">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>STT</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Học sinh</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Ngày</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Thời gian</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((r, i) => {
                  const dt = new Date(r.takenAt);
                  return (
                    <TableRow
                      key={r.id}
                      sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                    >
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{r.student?.name || "-"}</TableCell>
                      <TableCell>{dt.toLocaleDateString("vi-VN")}</TableCell>
                      <TableCell>{dt.toLocaleTimeString("vi-VN")}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    </div>
  );
};

export default AttendancePage;
